/**
 * Password strength validation configuration
 */
export interface PasswordConfig {
  minLength: number;
  requireUppercase: boolean;
  requireLowercase: boolean;
  requireNumbers: boolean;
  requireSpecial: boolean;
  specialChars: string;
}

/**
 * Default password configuration - matches backend
 */
export const PASSWORD_CONFIG: PasswordConfig = {
  minLength: 15,
  requireUppercase: true,
  requireLowercase: true,
  requireNumbers: true,
  requireSpecial: true,
  specialChars: '!@#$%^&*-_=+',
};

/**
 * Username validation configuration
 */
export interface UsernameConfig {
  minLength: number;
  maxLength: number;
}

/**
 * Default username configuration - matches backend
 */
export const USERNAME_CONFIG: UsernameConfig = {
  minLength: 3,
  maxLength: 128,
};

/**
 * Validation result type
 */
export interface ValidationResult {
  isValid: boolean;
  errors: string[];
}

/**
 * Validate password strength
 * @param password - Password to validate
 * @param config - Password configuration (uses default if not provided)
 * @returns Validation result with isValid flag and error list
 */
export function validatePassword(
  password: string,
  config: PasswordConfig = PASSWORD_CONFIG
): ValidationResult {
  const errors: string[] = [];

  if (!password) {
    errors.push("Password cannot be empty");
    return { isValid: false, errors };
  }

  // Check minimum length
  if (password.length < config.minLength) {
    errors.push(`Password must be at least ${config.minLength} characters long`);
  }

  // Check uppercase
  if (config.requireUppercase && !/[A-Z]/.test(password)) {
    errors.push("Password must contain at least one uppercase letter (A-Z)");
  }

  // Check lowercase
  if (config.requireLowercase && !/[a-z]/.test(password)) {
    errors.push("Password must contain at least one lowercase letter (a-z)");
  }

  // Check numbers
  if (config.requireNumbers && !/[0-9]/.test(password)) {
    errors.push("Password must contain at least one number (0-9)");
  }

  // Check special characters
  if (config.requireSpecial) {
    const specialRegex = new RegExp(`[${config.specialChars.replace(/[-[\]{}()*+?.,\\^$|#\s]/g, '\\$&')}]`);
    if (!specialRegex.test(password)) {
      errors.push(`Password must contain at least one special character (${config.specialChars})`);
    }
  }

  return { isValid: errors.length === 0, errors };
}

/**
 * Validate username
 * @param username - Username to validate
 * @param config - Username configuration (uses default if not provided)
 * @returns Validation result with isValid flag and error list
 */
export function validateUsername(
  username: string,
  config: UsernameConfig = USERNAME_CONFIG
): ValidationResult {
  const errors: string[] = [];

  if (!username) {
    errors.push("Username cannot be empty");
    return { isValid: false, errors };
  }

  // Check minimum length
  if (username.length < config.minLength) {
    errors.push(`Username must be at least ${config.minLength} characters long`);
  }

  // Check maximum length
  if (username.length > config.maxLength) {
    errors.push(`Username must not exceed ${config.maxLength} characters`);
  }

  // Check allowed characters (alphanumeric, underscore, hyphen)
  if (!/^[a-zA-Z0-9_-]+$/.test(username)) {
    errors.push("Username can only contain letters, numbers, underscores, and hyphens");
  }

  return { isValid: errors.length === 0, errors };
}

/**
 * Get human-readable password requirements for UI display
 * @param config - Password configuration
 * @returns Object with requirement descriptions
 */
export function getPasswordGuidelines(config: PasswordConfig = PASSWORD_CONFIG): {
  minLength: string;
  uppercase: string | null;
  lowercase: string | null;
  numbers: string | null;
  special: string | null;
} {
  return {
    minLength: `At least ${config.minLength} characters`,
    uppercase: config.requireUppercase ? "One uppercase letter (A-Z)" : null,
    lowercase: config.requireLowercase ? "One lowercase letter (a-z)" : null,
    numbers: config.requireNumbers ? "One number (0-9)" : null,
    special: config.requireSpecial ? `One special character (${config.specialChars})` : null,
  };
}

/**
 * Get human-readable username requirements for UI display
 * @param config - Username configuration
 * @returns Object with requirement descriptions
 */
export function getUsernameGuidelines(config: UsernameConfig = USERNAME_CONFIG): {
  minLength: string;
  maxLength: string;
  characters: string;
} {
  return {
    minLength: `At least ${config.minLength} characters`,
    maxLength: `Maximum ${config.maxLength} characters`,
    characters: "Letters, numbers, underscores, and hyphens only",
  };
}

/**
 * Generate a random password with the specified length
 * @param length - Password length (default: 15)
 * @param config - Password configuration for character requirements
 * @returns Random password containing uppercase, lowercase, numbers, and special characters
 */
export function generateRandomPassword(
  length: number = 15,
  config: PasswordConfig = PASSWORD_CONFIG
): string {
  const uppercase = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
  const lowercase = 'abcdefghijklmnopqrstuvwxyz';
  const numbers = '0123456789';
  const special = config.specialChars;
  const allChars = uppercase + lowercase + numbers + special;

  let password = '';
  
  // Ensure at least one character from each type
  password += uppercase[Math.floor(Math.random() * uppercase.length)];
  password += lowercase[Math.floor(Math.random() * lowercase.length)];
  password += numbers[Math.floor(Math.random() * numbers.length)];
  password += special[Math.floor(Math.random() * special.length)];

  // Fill the rest randomly
  for (let i = password.length; i < length; i++) {
    password += allChars[Math.floor(Math.random() * allChars.length)];
  }

  // Shuffle the password
  return password
    .split('')
    .sort(() => Math.random() - 0.5)
    .join('');
}
